import { Loader2 } from "lucide-react";
import { createPortal } from "react-dom";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import TailwindBottomButtonView from "../../../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Form } from "../../../../../components/ui/common/form";
import CommonSplitContainer from "../../../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import FacilityRegisterModal from "../../../../facility/facilityRegister/FacilityRegisterModal";
import ExternalStorageSectionCollapsible from "../externalStorage/components/ExternalStorageSectionCollapsible";
import ExternalStorageFacilityViewModal from "../externalStorage/facility/ExternalStorageFacilityViewModal";
import InternalStorageDetailsSection from "./components/InternalStorageDetailsSection";
import InternalStorageMetricsSection from "./components/InternalStorageMetricsSection";
import InternalStorageNotesSection from "./components/InternalStorageNotesSection";
import InternalStorageOriginalDataPanel from "./components/InternalStorageOriginalDataPanel";
import {
  InternalStorageGate,
  InternalStorageModalData,
  InternalStorageToolVariant
} from "./internalStorage.types";
import { resolveFacilityViewId } from "./internalStorage.utils";
import { useInternalStorageBaseScreen } from "./hooks/useInternalStorageBaseScreen";

interface InternalStorageBaseScreenProps {
  modalData: InternalStorageModalData;
  gate: InternalStorageGate;
  variant?: InternalStorageToolVariant;
}

const InternalStorageBaseScreen = ({
  modalData,
  gate,
  variant = "mapping"
}: InternalStorageBaseScreenProps) => {
  const screen = useInternalStorageBaseScreen({ modalData, gate, variant });

  if (screen.isInitialLoading) {
    return (
      <div
        className="tw-flex tw-items-center tw-justify-center tw-py-12"
        aria-busy="true"
        aria-live="polite"
      >
        <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
      </div>
    );
  }

  const facilityViewId = resolveFacilityViewId(screen.watchedWarehouseName);

  const handleViewFacility = () => {
    if (!facilityViewId) {
      return;
    }
    screen.setVisibleFacilityModal(true);
  };

  return (
    <>
      <div className="tw-relative" aria-busy={screen.isSaving}>
        {screen.isSaving && (
          <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-background/50">
            <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
          </div>
        )}
        <Form {...screen.form}>
          <form id="internal-storage-form" onSubmit={screen.handleSubmit(screen.onSubmit)}>
            <CommonSplitContainer
              topChild={
                <div className="tw-flex tw-flex-col tw-gap-5 tw-pr-1">
                  {screen.variant === "simulate" && (
                    <InternalStorageOriginalDataPanel
                      originalData={screen.originalData}
                      searchFacilities={screen.searchFacilities}
                    />
                  )}

                  <ExternalStorageSectionCollapsible
                    title="Storage details"
                    expanded={screen.detailsExpanded}
                    disabled={screen.isFormDisabled}
                    onToggle={() => screen.setDetailsExpanded(prev => !prev)}
                  >
                    <InternalStorageDetailsSection
                      register={screen.register}
                      control={screen.control}
                      errors={screen.errors}
                      disabled={screen.isFormDisabled}
                      variantConfig={screen.variantConfig}
                      facilities={screen.facilities}
                      searchFacilities={screen.searchFacilities}
                      facilityLoading={screen.facilityLoading}
                      onFacilityChange={screen.handleFacilityChange}
                      onViewFacility={handleViewFacility}
                      nameDisabled={screen.nameDisabled}
                      selectedFacilityId={facilityViewId ?? undefined}
                    />
                  </ExternalStorageSectionCollapsible>

                  <ExternalStorageSectionCollapsible
                    title="Storage metrics"
                    expanded={screen.metricsExpanded}
                    disabled={screen.isFormDisabled}
                    onToggle={() => screen.setMetricsExpanded(prev => !prev)}
                  >
                    <InternalStorageMetricsSection
                      register={screen.register}
                      control={screen.control}
                      errors={screen.errors}
                      disabled={screen.isFormDisabled}
                      variantConfig={screen.variantConfig}
                      isDefaultDataType={screen.isDefaultDataType}
                    />
                  </ExternalStorageSectionCollapsible>

                  {screen.variantConfig.supportsNotes && (
                    <ExternalStorageSectionCollapsible
                      title="Notes & documentation"
                      expanded={screen.notesExpanded}
                      disabled={screen.lockStatus}
                      onToggle={() => screen.setNotesExpanded(prev => !prev)}
                    >
                      <InternalStorageNotesSection
                        notesRef={screen.notesRef}
                        parentId={screen.notesParentId}
                        disabled={screen.lockStatus || screen.notesDisabled}
                      />
                    </ExternalStorageSectionCollapsible>
                  )}
                </div>
              }
              bottomChild={
                <TailwindBottomButtonView
                  deleteStatus={screen.showDeleteButton}
                  saveButtonStatus={true}
                  deleteButton={() => screen.setVisibleDeleteModal(true)}
                  deleteDisable={screen.deleteLockStatus}
                  saveTitle={screen.saveTitle}
                  type="submit"
                  saveDisable={screen.saveDisabled || screen.isSaving}
                  saveLoading={screen.isSaving}
                  customHeight={72}
                  customBottomContentMain="!tw-px-2 tw-py-3"
                  testId="internal-storage-bottom-button-view"
                  deleteButtonVariant="ghost"
                  showDeleteIcon
                />
              }
              bottomClassName="tw-border-t tw-border-border tw-bg-background"
            />
          </form>
        </Form>
      </div>

      {screen.visibleDeleteModal &&
        createPortal(
          <ConfirmModal
            viewModal={screen.visibleDeleteModal}
            closeModal={() => screen.setVisibleDeleteModal(false)}
            cancel={() => screen.setVisibleDeleteModal(false)}
            approve={screen.handleDelete}
            title={screen.gateConfig.deleteTitle}
            subTitle={screen.gateConfig.deleteSubtitle}
          />,
          document.body
        )}

      {screen.showFacilityRegister && screen.variantConfig.supportsNewFacility && (
        <FacilityRegisterModal
          visibleModal={screen.showFacilityRegister}
          setVisibleModal={screen.handleFacilityModalClose}
          refetchFacilityList={screen.refetchFacilityList}
        />
      )}

      {screen.visibleFacilityModal && facilityViewId && (
        <ExternalStorageFacilityViewModal
          isOpen={screen.visibleFacilityModal}
          onClose={() => screen.setVisibleFacilityModal(false)}
          facilityId={facilityViewId}
          variant={screen.variant}
        />
      )}
    </>
  );
};

export default InternalStorageBaseScreen;
